import { useState, type FormEvent } from "react";

import { useAuth } from "@/context/auth-context";
import { ApiError } from "@/lib/api-client";

/**
 * The sign-in screen, and the `forbidden` wall behind it.
 *
 * A full page rather than a dialog — the opposite of the storefront's choice,
 * and for the opposite reason. On the shop, sign-in interrupts someone
 * mid-purchase and must return them to where they were. Here it *is* the
 * destination: there is nothing to look at until you are through it.
 *
 * The API answers `INVALID_CREDENTIALS` identically for a wrong password, an
 * unknown address, and a Google-only account. That is deliberate on its side —
 * distinguishing them turns this form into an oracle for which addresses have
 * accounts — so this renders whatever came back rather than guessing.
 */
export function SignIn() {
  const { status, user, signIn, signOut } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);

    try {
      await signIn(email, password);
    } catch (thrown) {
      setError(
        thrown instanceof ApiError ? thrown.message : "Could not sign in. Try again shortly.",
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * Signed in, and not an admin.
   *
   * Worth its own state rather than a generic failure: the person is who they
   * say they are, so telling them to try again would be a lie. The only useful
   * action is to sign out — quite possibly they used a customer account by
   * mistake on a shared browser.
   */
  if (status === "forbidden") {
    return (
      <main className="flex min-h-full items-center justify-center p-6">
        <div className="w-full max-w-[380px] rounded-xl border border-line bg-surface p-7 text-center">
          <h1 className="text-[17px] font-semibold text-ink">Not an admin account</h1>
          <p className="mt-2 text-[13px] leading-relaxed text-muted">
            {user?.email ?? "This account"} is signed in but does not have admin access.
          </p>
          <button
            type="button"
            onClick={() => void signOut()}
            className="mt-5 w-full rounded-lg bg-ink px-4 py-2.5 text-[13.5px] font-semibold text-white"
          >
            Sign out
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-[380px] rounded-xl border border-line bg-surface p-7">
        <p className="text-[11px] font-bold tracking-[0.14em] text-faint uppercase">Arrow</p>
        <h1 className="mt-1 text-[19px] font-semibold text-ink">Admin console</h1>
        <p className="mt-1.5 mb-6 text-[13px] leading-relaxed text-muted">
          Sign in with an account that has admin access.
        </p>

        <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-semibold text-ink">Email</span>
            <input
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="rounded-lg border border-line px-3 py-2.5 text-[14px] outline-none focus:border-ink"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-semibold text-ink">Password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="rounded-lg border border-line px-3 py-2.5 text-[14px] outline-none focus:border-ink"
            />
          </label>

          {error ? (
            <p role="alert" className="text-[12.5px] leading-relaxed text-bad">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={busy}
            className="mt-1 rounded-lg bg-ink px-4 py-2.5 text-[13.5px] font-semibold text-white disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        {/* Google sign-in is the better default for staff — it inherits the
            organisation's own 2FA rather than relying on a password policy this
            API would have to enforce (ADMIN.md §6). Not wired yet; the button
            component can be vendored from arrow-fe when it is. */}
      </div>
    </main>
  );
}
